import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { MapContainer, TileLayer, GeoJSON, LayersControl, useMap, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { FaCircleInfo } from "react-icons/fa6";
import "./LSM.css";

import { Box, Typography } from '@mui/material';
import L from "leaflet";
import parseGeoraster from "georaster";
import GeoRasterLayer from "georaster-layer-for-leaflet";
import centroid from "@turf/centroid";
import CommonTable from "../../../../common/CommonTable";
import { MapHomeControl, MapLegendControl } from "../../../../common/MapControls/MapControls";

const INITIAL_VIEW_STATE = {
  longitude: 125.7275,
  latitude: -8.8742,
  zoom: 8,
};

const RISK_CATEGORIES = [
  { label: "No-Risk", key: "No-Risk", color: "rgb(148, 203, 85)" },
  { label: "Low-Risk", key: "Low-Risk", color: "rgb(243, 235, 18)" },
  { label: "Medium-Risk", key: "Medium-Risk", color: "rgb(243, 155, 58)" },
  { label: "High-Risk", key: "High-Risk", color: "rgb(230, 45, 58)" },
  { label: "No Data", key: "No Data", color: "rgb(222,222,223)" }
];

const InfoButton = ({ onClick }) => {
  // Leaflet's own drag/click handling swallows clicks on overlay controls
  // unless propagation to the map is explicitly disabled on the DOM node.
  const btnRef = useCallback((node) => {
    if (node) {
      L.DomEvent.disableClickPropagation(node);
      L.DomEvent.disableScrollPropagation(node);
    }
  }, []);
  return (
    <div
      ref={btnRef}
      style={{
        position: "absolute",
        bottom: "20px",
        left: "12px",
        padding: "8px",
        borderRadius: "8px",
        fontSize: "15px",
        cursor: "pointer",
        background: "rgba(255, 255, 255, 0.9)",
        backdropFilter: "blur(10px) brightness(1.1)",
        WebkitBackdropFilter: "blur(10px) brightness(1.1)",
        border: "1px solid rgba(0, 0, 0, 0.08)",
        color: "#333333",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
        transition: "all 0.2s ease",
        zIndex: 1000,
      }}
      onClick={onClick}
    >
      <FaCircleInfo />
    </div>
  );
};

const Legend = () => (
  <MapLegendControl sections={[{ items: RISK_CATEGORIES }]} />
);

const MapBoundsUpdater = ({ bounds }) => {
  const map = useMap();

  useEffect(() => {
    if (bounds) {
      map.fitBounds(bounds);
    }
  }, [map, bounds]);

  return null;
};

const GeoTiffLayerComponent = ({ georaster }) => {
  const map = useMap();

  useEffect(() => {
    if (!georaster || !map) return;
    const colorFn = values => {
      const pixelValue = values[0];
      if (pixelValue === 4) return 'rgb(230, 45, 58)';
      if (pixelValue === 3) return 'rgb(243, 155, 58)';
      if (pixelValue === 2) return 'rgb(243, 235, 18)';
      if (pixelValue === 1) return 'rgb(148, 203, 85)';
      return null;
    };
    const layer = new GeoRasterLayer({
      georaster: georaster,
      opacity: 0.7,
      pixelValuesToColorFn: colorFn,
      resolution: 256,
    });
    layer.addTo(map);
    return () => {
      if (map.hasLayer(layer)) {
        map.removeLayer(layer);
      }
    };
  }, [map, georaster]);

  return null;
};

const LSM = () => {
  const [boundaryData, setBoundaryData] = useState(null);
  const [info, setInfo] = useState(false);
  const infoRef = useRef(null);
  const infoPanelRef = useCallback((node) => {
    infoRef.current = node;
    if (node) {
      L.DomEvent.disableClickPropagation(node);
      L.DomEvent.disableScrollPropagation(node);
    }
  }, []);
  useEffect(() => {
    if (!info) return;
    const handleClickOutside = (e) => {
      if (infoRef.current && !infoRef.current.contains(e.target)) setInfo(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [info]);
  const [georaster, setGeoraster] = useState(null);
  const [riskDetails, setRiskDetails] = useState([]);
  const [clickedSucoPopup, setClickedSucoPopup] = useState(null);
  const [isLoadingTiff, setIsLoadingTiff] = useState(false);
  const [mapBounds, setMapBounds] = useState(null);

  const tiffUrl = "/DATA/Landslide/Susceptibility/LSM/TimorLeste_landslide_sus_static.tif";

  const tableData = useMemo(
    () => riskDetails.map((item) => ({ ...item, advisory: item.riskLevel })),
    [riskDetails],
  );

  const legendColors = useMemo(
    () =>
      RISK_CATEGORIES.reduce(
        (acc, cat) => ({ ...acc, [cat.key]: cat.color }),
        { All: '#f0f0f0' },
      ),
    [],
  );

  const columns = useMemo(
    () => [
      { key: 'SlNo', label: 'S.No.' },
      { key: 'ADM1', label: 'Municipality', sortable: true },
      { key: 'ADM2', label: 'Post Admin', sortable: true },
      { key: 'ADM3', label: 'Suco', sortable: true },
      {
        key: 'riskLevel',
        label: 'Risk Level',
        sortable: true,
        render: (row) => {
          const category = RISK_CATEGORIES.find(cat => cat.key === row.riskLevel);
          const backgroundColor = category ? category.color : 'grey';
          return (
            <span
              style={{
                backgroundColor: backgroundColor,
                color: 'black',
                fontWeight: 'bold',
                padding: '2px 5px',
                borderRadius: '3px',
                display: 'inline-block'
              }}
            >
              {row.riskLevel}
            </span>
          );
        },
      },
      {
        key: 'Population',
        label: 'Total Population',
        sortable: true,
        render: (row) => row.Population?.toLocaleString() || 'N/A',
      },
      {
        key: 'Male',
        label: 'Total Male',
        sortable: true,
        render: (row) => row.Male?.toLocaleString() || 'N/A',
      },
      {
        key: 'Female',
        label: 'Total Female',
        sortable: true,
        render: (row) => row.Female?.toLocaleString() || 'N/A',
      },
      {
        key: 'HH_Urban',
        label: 'HH Urban',
        sortable: true,
        render: (row) => row.HH_Urban?.toLocaleString() || 'N/A',
      },
      {
        key: 'HH_Rural',
        label: 'HH Rural',
        sortable: true,
        render: (row) => row.HH_Rural?.toLocaleString() || 'N/A',
      },
      {
        key: 'Household',
        label: 'Households',
        sortable: true,
        render: (row) => row.Household?.toLocaleString() || 'N/A',
      },
    ],
    [],
  );


  useEffect(() => {
    const fetchBoundaryData = async () => {
      try {
        const response = await fetch("/DATA/OSMbased_adm3_boundary.json");
        const json = await response.json();
        setBoundaryData(json);
        
        if (json && json.features && json.features.length > 0) {
          const geoJsonLayer = L.geoJSON(json);
          setMapBounds(geoJsonLayer.getBounds());
        }
        
        console.log("Boundary data loaded.");
      } catch (error) {
        console.error("Error loading boundary data:", error);
      }
    };
    fetchBoundaryData();
  }, []);

  useEffect(() => {
    const fetchTiffData = async () => {
      setIsLoadingTiff(true);
      setGeoraster(null);

      try {
        console.log("Fetching GeoTIFF from:", tiffUrl);
        const response = await fetch(tiffUrl);
        if (!response.ok) {
          throw new Error(`Failed to fetch GeoTIFF: ${response.status} ${response.statusText}`);
        }
        const arrayBuffer = await response.arrayBuffer();
        const parsedGeoraster = await parseGeoraster(arrayBuffer);
        setGeoraster(parsedGeoraster);
        console.log("GeoTIFF loaded and parsed.");
      } catch (error) {
        console.error("Error loading or parsing GeoTIFF:", error);
      } finally {
        setIsLoadingTiff(false);
      }
    };
    if (tiffUrl) {
      fetchTiffData();
    }
  }, [tiffUrl]);

  useEffect(() => {
    if (!georaster || !boundaryData) {
        console.log("Waiting for georaster and boundaryData to load for analysis.");
        return;
    }

    console.log("Starting risk analysis: Using Centroid Sampling for Suco Risk Level.");
    let identifiedSucoDetails = [];
    const { xmin, ymax, pixelWidth, pixelHeight, values } = georaster;
    const rasterValues = values[0];

    boundaryData.features.forEach(feature => {
      if (feature.geometry && feature.properties) {
        const featureCentroid = centroid(feature.geometry);
        const [lng, lat] = featureCentroid.geometry.coordinates;

        const x = Math.floor((lng - xmin) / pixelWidth);
        const y = Math.floor((ymax - lat) / pixelHeight);

        let riskLevel;
        let riskColor;

        if (x >= 0 && x < georaster.width && y >= 0 && y < georaster.height) {
          if (rasterValues && rasterValues[y] !== undefined && rasterValues[y][x] !== undefined) {
            const pixelValue = rasterValues[y][x];

            switch (pixelValue) {
              case 4:
                riskLevel = 'High-Risk';
                riskColor = 'rgb(230, 45, 58)';
                break;
              case 3:
                riskLevel = 'Medium-Risk';
                riskColor = 'rgb(243, 155, 58)';
                break;
              case 2:
                riskLevel = 'Low-Risk';
                riskColor = 'rgb(243, 235, 18)';
                break;
              case 1:
                riskLevel = 'No-Risk';
                riskColor = 'rgb(148, 203, 85)';
                break;
              default:
                riskLevel = 'No Data';
                riskColor = 'lightgrey';
                console.warn(`Unexpected pixel value for ${feature.properties.ADM3}: ${pixelValue}. Assigning to 'No Data'.`);
                break;
            }
          } else {
            riskLevel = 'No Data';
            riskColor = 'lightgrey';
          }
        } else {
            riskLevel = 'No Data';
            riskColor = 'lightgrey';
        }

        const sucoData = {
          ...feature.properties,
          riskLevel: riskLevel,
          riskColor: riskColor
        };
        identifiedSucoDetails.push(sucoData);
      }
    });

    identifiedSucoDetails.sort((a, b) => {
        if (a.ADM1 < b.ADM1) return -1;
        if (a.ADM1 > b.ADM1) return 1;
        if (a.ADM2 < b.ADM2) return -1;
        if (a.ADM2 > b.ADM2) return 1;
        return a.ADM3.localeCompare(b.ADM3);
    });

    identifiedSucoDetails = identifiedSucoDetails.map((item, index) => ({
      ...item,
      sNo: index + 1
    }));

    setRiskDetails(identifiedSucoDetails);
    console.log(`Risk analysis complete. Processed ${identifiedSucoDetails.length} sucos.`);
  }, [georaster, boundaryData]);

  const handleSucoClick = useCallback((feature, latlng) => {
    const properties = feature.properties;

    const sucoDetailFromAnalysis = riskDetails.find(
      (detail) =>
        detail.ADM1 === properties.ADM1 &&
        detail.ADM2 === properties.ADM2 &&
        detail.ADM3 === properties.ADM3
    );

    const displayDetails = sucoDetailFromAnalysis || {
        ...properties,
        riskLevel: 'No Data',
        riskColor: 'lightgrey',
    };

    console.log("Displaying details for popup:", displayDetails);

    setClickedSucoPopup({
      position: latlng,
      details: displayDetails,
    });
  }, [riskDetails]);

  const onEachSucoFeature = useCallback((feature, layer) => {
    layer.on({
      click: (e) => {
        handleSucoClick(feature, e.latlng);
      },
    });
  }, [handleSucoClick]);

  const sucoStyle = useCallback((feature) => {
    if (!feature || !feature.properties || riskDetails.length === 0) {
      return {
        color: "#000000",
        weight: 1,
        opacity: 0.8,
        fillOpacity: 0.05,
        fillColor: 'lightgrey'
      };
    }

    const sucoAnalysis = riskDetails.find(
      (detail) =>
        detail.ADM1 === feature.properties.ADM1 &&
        detail.ADM2 === feature.properties.ADM2 &&
        detail.ADM3 === feature.properties.ADM3
    );

    const effectiveRiskLevel = sucoAnalysis?.riskLevel || 'No Data';
    const category = RISK_CATEGORIES.find(cat => cat.key === effectiveRiskLevel);
    const color = category ? category.color : 'lightgrey';
    const fillOpacity = effectiveRiskLevel === 'No Data' ? 0.05 : 0.4;

    return {
      color: "#000000",
      weight: 1,
      opacity: 0.8,
      fillColor: color,
      fillOpacity: fillOpacity,
    };
  }, [riskDetails]);

  return (
    <div className="wind-speed-cyclone-container">
      <div className="map-container-WindSpeedCyclone">
        <MapContainer
          center={[INITIAL_VIEW_STATE.latitude, INITIAL_VIEW_STATE.longitude]}
          zoom={INITIAL_VIEW_STATE.zoom}
          className="mapcontainerall"
        >
          <MapHomeControl />
          <MapBoundsUpdater bounds={mapBounds} />
          
          {isLoadingTiff && (
            <div style={{position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 1001, background: 'rgba(255,255,255,0.8)', padding: '10px', borderRadius: '5px'}}>Loading Map Data...</div>
          )}

          {info && (
            <div
              ref={infoPanelRef}
              style={{
                position: "absolute",
                bottom: "70px",
                left: "12px",
                fontSize: "12px",
                zIndex: 1000,
                padding: "10px 14px",
                borderRadius: "10px",
                background: "rgba(255, 255, 255, 0.9)",
                backdropFilter: "blur(10px) brightness(1.1)",
                WebkitBackdropFilter: "blur(10px) brightness(1.1)",
                border: "1px solid rgba(0, 0, 0, 0.08)",
                boxShadow: "0 4px 16px rgba(0, 0, 0, 0.15), inset 0 1px 2px rgba(255, 255, 255, 0.5)",
                color: "#333333",
                fontWeight: "500",
                maxWidth: "320px",
                lineHeight: "1.4",
              }}
            >
              Landslide susceptibility model using Random Forest Model (LSM) and properties used slope, aspect, curvature, ndvi, rainfall(chirps), distRoad, distStream, lulc.
            </div>
          )}
          <InfoButton onClick={() => setInfo(!info)} />

          <LayersControl position="topright">
            <LayersControl.BaseLayer name="White Theme" checked>
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer name="OpenStreetMap">
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
            </LayersControl.BaseLayer>

            <LayersControl.Overlay name="Landslide Susceptibility" checked>
              {georaster && !isLoadingTiff && <GeoTiffLayerComponent georaster={georaster} />}
            </LayersControl.Overlay>

            {boundaryData && (
              <LayersControl.Overlay name="Suco Boundaries" checked>
                <GeoJSON
                  key={riskDetails.length > 0 ? "boundaries-loaded-with-risk" : "boundaries-loading-risk"}
                  data={boundaryData}
                  style={sucoStyle}
                  onEachFeature={onEachSucoFeature}
                />
              </LayersControl.Overlay>
            )}
          </LayersControl>

          {clickedSucoPopup && clickedSucoPopup.details && (
            <Popup
              position={clickedSucoPopup.position}
              onClose={() => setClickedSucoPopup(null)}
            >
              <div style={{overflowY: 'auto', paddingRight: '10px'}}>
                <h5>Suco Details:</h5>
                <p><strong>Municipality:</strong> {clickedSucoPopup.details.ADM1}</p>
                <p><strong>Post Admin:</strong> {clickedSucoPopup.details.ADM2}</p>
                <p><strong>Suco:</strong> {clickedSucoPopup.details.ADM3}</p>
                <hr style={{margin: '5px 0'}}/>
                <p><strong>Total Population:</strong> {clickedSucoPopup.details.Population?.toLocaleString() || 'N/A'}</p>
                <p><strong>Total Male:</strong> {clickedSucoPopup.details.Male?.toLocaleString() || 'N/A'}</p>
                <p><strong>Total Female:</strong> {clickedSucoPopup.details.Female?.toLocaleString() || 'N/A'}</p>
                <p><strong>Households:</strong> {clickedSucoPopup.details.Household?.toLocaleString() || 'N/A'}</p>
                {clickedSucoPopup.details.riskLevel && (
                  <p><strong>Risk Level:</strong>
                    <span style={{
                      backgroundColor: clickedSucoPopup.details.riskColor,
                      color: 'black',
                      fontWeight: 'bold',
                      padding: '2px 5px',
                      marginLeft: '5px',
                      borderRadius: '3px',
                      display: 'inline-block'
                    }}>
                      {clickedSucoPopup.details.riskLevel}
                    </span>
                  </p>
                )}
                {clickedSucoPopup.details.riskLevel === 'No Data' && (
                  <p style={{color: 'grey', fontStyle: 'italic', fontSize: '0.9em'}}>
                    Risk analysis not available for this area (e.g., no data, outside analysis extent, or invalid pixel).
                  </p>
                )}
              </div>
            </Popup>
          )}
          <Legend />
        </MapContainer>

        <Box sx={{ p: 2, borderTop: '1px solid #ddd', overflow: 'auto' }}>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
                Suco-level Risk Details
            </Typography>
            <CommonTable
                tableData={tableData}
                columns={columns}
                showFilter
                filterOptions={['All', ...RISK_CATEGORIES.map(cat => cat.key)]}
                legendColors={legendColors}
                searchableKeys={['ADM1', 'ADM2', 'ADM3']}
                tableName="Landslide_LSM"
                searchPlaceholder="Search by Municipality, Post Admin, Suco..."
                emptyMessage="No suco risk data found."
            />
        </Box>
      </div>
    </div>
  );
};

export default LSM;